import React from "react";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { Image } from "../../../api/contracts/images";

interface PackageRoomRowProps {
  name: string;
  description: string | null;
  image: Image | null;
  /** Unit label, when the package stages more than one. */
  label?: string | null;
  /** Reopens the choice. Omitted where there is nothing to reopen — a package
   *  bound to a single category. */
  onChange?: () => void;
  /** Opens a fuller look at the room. An info icon, matching the landing
   *  card's category chips — the same gesture should carry the same mark, and
   *  the expand glyph read as "fullscreen" rather than "tell me about it". */
  onPreview?: () => void;
  /** Rendered under the name — used for a room that cannot be taken. */
  note?: string | null;
  /** Dims the row without hiding it: the room is part of the package, it just
   *  isn't available for this stay. */
  muted?: boolean;
}

/**
 * A room the guest has settled on, shown the same way wherever it appears: the
 * package's "what's included" list and the mobile search sheet's category step.
 * Same shape as the included-product cards, because by this point the room is
 * part of what the package gives you rather than a selection to review.
 */
export const PackageRoomRow: React.FC<PackageRoomRowProps> = ({
  name,
  description,
  image,
  label,
  onChange,
  onPreview,
  note,
  muted = false,
}) => {
  const { t } = useTranslation();

  return (
    <div
      data-testid="package-room-row"
      className={`flex items-start gap-3 rounded-2xl border border-border bg-surface-card p-3 ${
        muted ? "opacity-60" : ""
      }`}
    >
      {image?.url && (
        <img
          src={image.url}
          alt={image.description ?? ""}
          className="h-14 w-14 shrink-0 rounded-lg object-cover"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-ink [overflow-wrap:anywhere]">
          {label ? `${label} · ${name}` : name}
        </span>
        {description && (
          <p className="line-clamp-3 whitespace-pre-line break-words text-xs leading-[1.3] text-ink-secondary">
            {description}
          </p>
        )}
        {note && (
          <span
            data-testid="package-room-row-note"
            className="text-xs font-medium text-accent"
          >
            {note}
          </span>
        )}
      </div>
      {onPreview && (
        <button
          type="button"
          onClick={onPreview}
          aria-label={t("landing.package_card.preview_category", { name })}
          data-testid="package-room-row-preview"
          className="flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-full text-ink-secondary hover:bg-surface-card-secondary"
        >
          <FiInfo className="h-4 w-4" />
        </button>
      )}
      {onChange && (
        <button
          type="button"
          onClick={onChange}
          data-testid="package-room-row-change"
          className="shrink-0 self-center text-sm font-medium text-ink underline"
        >
          {t("landing.package_detail.change_category")}
        </button>
      )}
    </div>
  );
};
